import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AssetStateService} from "../../assets/asset.state.service";
import {AssetResultComponent} from "../../assets/asset-result/asset-result.component";
import {Asset} from "../../assets/asset.model";
import {Subscription} from "rxjs";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {CollectionService} from "../collection.service";
import {Collection, CollectionData} from "../collection.model";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatButton, MatIconButton} from "@angular/material/button";
import {AssetRenderStyle} from "../../assets/asset-render-style";
import {FilterStateService} from "../../dam-header/filter-state.service";
import {Location} from "@angular/common";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {CollectionEditComponent} from "../collection-overview/collection-edit/collection-edit.component";
import {ConfirmComponent} from "../../common/confirme/confirm.component";
import {ConfirmData} from "../../common/confirm-data";
import {TranslatePipe} from "@ngx-translate/core";
import {MatIcon} from "@angular/material/icon";
import {NavigationPoint} from '../../navigation-point';
import {NavStatusService} from '../../nav-status.service';
import {CollectionAccessComponent} from "../collection-overview/collection-access/collection-access.component";
import {CollectionShareComponent} from "../collection-overview/collection-share/collection-share.component";
import {MatFormField, MatInput, MatSuffix} from "@angular/material/input";
import {SnackBarService} from "../../common/snack-bar-service";
import {CollectionTransferComponent} from "../collection-overview/collection-transfer/collection-transfer.component";
import {QueryParamsToFilterService} from "../../common/query-params-to-filter.service";
import {DateLocalePipe} from "../../common/pipes/date-locale.pipe";
import {User} from "../../user/user";
import {UserStateService} from "../../user/user.state.service";

@Component({
    selector: 'app-collection-view',
    imports: [
        AssetResultComponent,
        MatMenu,
        MatMenuTrigger,
        MatMenuItem,
        MatButton,
        TranslatePipe,
        MatIcon,
        MatInput,
        MatIconButton,
        MatFormField,
        MatSuffix,
        DateLocalePipe,
    ],
    templateUrl: './collection-view.component.html',
    styleUrl: './collection-view.component.scss'
})
export class CollectionViewComponent implements OnInit, OnDestroy {

    @Input() id: string = '';

    collection: Collection | null = null;

    totalAssets = 0;
    private assetSub!: Subscription;
    pageInfoSubscription!: Subscription;
    paramSubscription: Subscription | undefined;
    queryParamSubscription: Subscription | undefined;
    useDefaultFilter: boolean = true;
    paramsParsed: boolean = false;
    qParamsParsed: boolean = false;
    qParams: Params | undefined;
    user: User | null | undefined
    userSubscription: Subscription | undefined;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private collectionService: CollectionService,
        private assetStateService: AssetStateService,
        private filterStateService: FilterStateService,
        private navStatusService: NavStatusService,
        private snackBarService: SnackBarService,
        private paramsToFilterService: QueryParamsToFilterService,
        private location: Location,
        private userService: UserStateService,
    ) {
        this.userSubscription = this.userService.user$.subscribe(user => {
            this.user = user;
        });
    }

    protected readonly AssetRenderStyle = AssetRenderStyle;

    get assets(): Asset[] {
        return this.assetStateService.loadedAssets;
    }

    ngOnInit(): void {
        this.navStatusService.setActiveNavPoint(NavigationPoint.collections_details);
        this.paramSubscription = this.activatedRoute.params.subscribe(params => {
            this.id = params["id"];
            this.paramsParsed = true;
            this.loadCollection();
        });
        this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(qParams => {
            if (this.isEmptyObject(qParams)) {
                this.useDefaultFilter = true;
            } else {
                if (!qParams['assetId']) {
                    this.useDefaultFilter = false;
                    this.qParams = qParams;
                }
                if (qParams['assetId']) {
                    this.useDefaultFilter = true;
                    this.assetStateService.assetUuidFromQueryParam = qParams['assetId'];
                }
            }
            this.qParamsParsed = true;
            this.loadCollection();
        })
        this.filterStateService.setShowFilterBar(true);
        this.pageInfoSubscription = this.assetStateService.pageInfo$.subscribe(res => {
            if (res) {
                this.totalAssets = res.totalItems;
            }
        })
    }
    isEmptyObject(params: Params): boolean {
        for(var prop in params) {
            if(params.hasOwnProperty(prop))
                return false;
        }
        return true;
    }

    private loadCollection() {
        if (this.paramsParsed && this.qParamsParsed) {
            this.collectionService.getCollection(this.id).subscribe({
                next: (collection) => {
                    this.collection = collection;
                    this.loadAssets();
                },
                error: (error) => {
                    this.snackBarService.showMessage({
                        message: 'error.unknown',
                        success: false
                    })
                }
            });
        }

    }

    private loadAssets() {
        this.assetStateService.collection$.next(this.collection);
        if (this.useDefaultFilter) {
            this.assetStateService.filter = this.assetStateService.createDefaultAssetFilter();
        } else {
            if (this.qParams) {
                this.paramsToFilterService.parseQueryParams(this.qParams);
                this.location.replaceState(
                    location.pathname, undefined
                )
                this.qParams = undefined;
                this.useDefaultFilter = true;
            }

        }

    }

    editCollection() {
        let dialogRef: MatDialogRef<CollectionEditComponent, CollectionData> = this.dialog.open(
            CollectionEditComponent,
            {
                data: {...this.collection},
                autoFocus: false
            }
        );
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.collectionService.updateCollection(this.collection!!.uuid, result).subscribe({
                    next: col => {
                        this.collection = col;
                    },
                    error: error => {
                        console.error('Error updating collection', error);
                        this.snackBarService.showMessage({
                            message: 'error.unknown',
                            success: false
                        });
                    }
                });
            }
        });
    }

    shareCollection() {
        let dialogRef = this.dialog.open(CollectionShareComponent, {
            data: this.collection,
            width: '600px',
            autoFocus: false
        });
        dialogRef.afterClosed().subscribe({
            next: (result) => {
                if (result) {
                    this.collection = result;
                }
            },
            error: (error) => {
                console.error('Error sharing collection', error);
                this.snackBarService.showMessage({
                    message: 'error.unknown',
                    success: false
                });
            }
        });
    }

    deleteCollection() {
        let dialogData: ConfirmData = {
            headline: 'confirm.deleteCollection',
            mess1: 'confirm.deleteCollectionMessage',
            mess2: '',
            mess3: '',
            btnText: 'button.delete'
        }
        let dialogRef: MatDialogRef<ConfirmComponent, CollectionData> = this.dialog.open(
            ConfirmComponent,
            {
                data: dialogData,
                autoFocus: false,
            }
        );
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.collectionService.deleteCollection(this.id).subscribe({
                    next: __ => {
                        this.snackBarService.showMessage({
                            message: 'collection.action.delete.success',
                            success: true
                        });
                        this.router.navigate(['/collections']);
                    },
                    error: error => {
                        console.error('Error deleting collection', error);
                        this.snackBarService.showMessage({
                            message: 'error.unknown',
                            success: false
                        });
                    }
                });
            }
        });
    }

    back() {
        this.router.navigate(['/collections']);
    }

    showAccess() {
        let dialogRef = this.dialog.open(CollectionAccessComponent, {
            data: this.collection,
            width: '600px',
            autoFocus: false
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.collection = result;
            }
        });
    }

    copyLink(url: string) {
        navigator.clipboard.writeText(url).then(() => {
           // console.log('Link copied to clipboard');
            this.snackBarService.showMessage({message: 'collection.linkcopied', success: true});
        }, (error) => {
           // console.error('Error copying link to clipboard', error);
        });
    }

    transferOwnership() {
        let dialogRef = this.dialog.open(CollectionTransferComponent, {
            data: this.collection,
            autoFocus: false,
            width: '600px'
            });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.collection = result;
                this.snackBarService.showMessage({
                    message: 'collection.action.transfer.success',
                    success: true
                });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.assetSub) {
            this.assetSub.unsubscribe();
        }
        if (this.pageInfoSubscription) {
            this.pageInfoSubscription.unsubscribe();
        }
        if (this.paramSubscription) {
            this.paramSubscription.unsubscribe();
        }
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }

    }
}
